import { Grid } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { useParams } from "react-router-dom";
import { getClaimIssues, getRequestClaims, sendClaim } from "../../../../api/ComplaintsAndClaims";
import CenterLoading from "../../../../components/CenterLoading/CenterLoading";
import FormModal from "../../../../components/FormModal/FormModal";
import Select from "../../../../components/Select/Select";
import TextField from "../../../../components/TextField/TextField";
import { HideGlobalLoading, ShowGlobalLoading } from "../../../../redux/actions/UiActions";
import COLORS from "../../../../theme/Colors";
import FONTS_FAMILY from "../../../../theme/FontsFamily";
import { FormSchema } from "./ComplaintsAndClaimsConstants";

function ComplaintsAndClaims({ companyID, companyName }) {
    const matchesWidth = useMediaQuery("(min-width:768px)");
    const history = useHistory();
    const dispatch = useDispatch();
    const { authenticated } = useSelector((state) => state.authReducer);
    const { enqueueSnackbar } = useSnackbar();
    let { requestID } = useParams();
    const queryClient = useQueryClient();

    const [claimModalVisible, setClaimModalVisible] = useState(false);

    const userData = queryClient.getQueryData("userData");
    const serviceRequestedDetail = queryClient.getQueryData(["serviceRequestedDetail", requestID]);
    const { isLoading: claimsDataIsLoading, data: claimsData } = useQuery(
        ["claimsData", serviceRequestedDetail.request.code],
        () => getRequestClaims(serviceRequestedDetail.request.code),
        {
            staleTime: 0,
            cacheTime: 0,
        }
    );
    const { isLoading: claimsIssuesIsLoading, data: claimsIssuesData } = useQuery(["claimsIssues"], () => getClaimIssues());
    const mutation = useMutation(sendClaim);

    const handleClaimModalVisibility = () => {
        setClaimModalVisible(!claimModalVisible);
    };

    const formik = useFormik({
        initialValues: {
            reason: "",
            message: "",
        },
        validationSchema: FormSchema,
        validateOnChange: true,
        validateOnBlur: true,
        enableReinitialize: true,
        onSubmit: (values) => {
            handleRegisterClaim(values);
        },
    });

    const handleRegisterClaim = (formData) => {
        dispatch(ShowGlobalLoading("Cargando"));
        mutation.mutate(
            {
                issue_id: formData.reason,
                citizen_id: userData.payload.citizen_id,
                service_id: serviceRequestedDetail.request.service_id,
                request_code: serviceRequestedDetail.request.code,
                citizen_name: userData.payload.name,
                description: formData.message,
                company_id: companyID,
                company_name: companyName,
            },
            {
                onSuccess: () => {
                    enqueueSnackbar("Reclamación creada con exito", { variant: "success", autoHideDuration: 2000 });
                    queryClient.invalidateQueries(["claimsData", serviceRequestedDetail.request.code]);
                    handleClaimModalVisibility();
                    formik.resetForm();
                },
                onError: () => {
                    enqueueSnackbar("Ha ocurrido un error", { variant: "error", autoHideDuration: 3000 });
                },
                onSettled: () => {
                    dispatch(HideGlobalLoading());
                },
            }
        );
    };

    const issuesData = claimsIssuesData?.map((item) => {
        return {
            label: item.content,
            value: item.id,
        };
    });

    const getStatusStyle = (status) => {
        const lower = (status || '').toLowerCase();
        if (lower.includes('resuelt') || lower.includes('cerrad')) {
            return { bg: '#dcfce7', color: '#15803d', border: '#86efac' };
        }
        if (lower.includes('pendiente') || lower.includes('abierta')) {
            return { bg: '#fef3c7', color: '#92400e', border: '#fcd34d' };
        }
        return { bg: '#f1f5f9', color: '#475569', border: '#e2e8f0' };
    };

    if (claimsIssuesIsLoading || claimsDataIsLoading) return <CenterLoading />;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {/* Modal */}
            <FormModal open={claimModalVisible} onClose={handleClaimModalVisibility} title="" maxWidth="sm">
                {/* Custom Modern Header */}
                <div className="claim-modal-header">
                    <div className="claim-modal-title-row">
                        <div className="claim-modal-icon-container">
                            ⚠️
                        </div>
                        <div>
                            <h3 className="claim-modal-title">
                                Nueva Reclamación
                            </h3>
                            <p className="claim-modal-subtitle">
                                Indique el motivo y detalle su queja para que nuestro equipo pueda darle seguimiento.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Form fields */}
                <div className="claim-modal-body">
                    <Select
                        id="reason"
                        title="Motivo de la reclamación"
                        value={formik.values.reason}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.reason && Boolean(formik.errors.reason)}
                        helperText={formik.touched.reason && formik.errors.reason}
                        required
                        data={issuesData}
                    />

                    <TextField
                        id="message"
                        title="Detalle u Observaciones"
                        placeholder="Describa de forma detallada y específica el motivo de su reclamación..."
                        value={formik.values.message}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.message && Boolean(formik.errors.message)}
                        helperText={formik.touched.message && formik.errors.message}
                        multiline
                        minRows={4}
                        required
                    />
                </div>

                {/* Modern Footer Actions */}
                <div className="claim-modal-footer">
                    <button
                        type="button"
                        className="claim-modal-cancel-btn"
                        onClick={handleClaimModalVisibility}
                    >
                        CANCELAR
                    </button>
                    <button
                        type="button"
                        className="claim-modal-submit-btn"
                        onClick={formik.handleSubmit}
                    >
                        ENVIAR RECLAMACIÓN
                    </button>
                </div>
            </FormModal>

            {/* Header Card */}
            <div style={{
                background: '#ffffff',
                borderRadius: '14px',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                overflow: 'hidden',
            }}>
                {/* Title + Button */}
                <div style={{
                    padding: '18px 24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '12px',
                    borderBottom: '1px solid #f1f5f9',
                    background: '#eef2ff',
                }}>
                    <h3 style={{
                        margin: 0,
                        fontSize: '17px',
                        fontWeight: 700,
                        color: COLORS.secondary,
                        fontFamily: FONTS_FAMILY.bold.body,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                    }}>
                        <span style={{ fontSize: '20px' }}>⚠️</span>
                        Reclamaciones
                        {claimsData?.length > 0 && (
                            <span style={{
                                fontSize: '12px',
                                background: COLORS.secondary,
                                color: '#fff',
                                borderRadius: '12px',
                                padding: '2px 10px',
                                fontWeight: 600,
                            }}>
                                {claimsData.length}
                            </span>
                        )}
                    </h3>
                    <button
                        onClick={handleClaimModalVisibility}
                        style={{
                            padding: '8px 20px',
                            borderRadius: '8px',
                            border: `1px solid ${COLORS.secondary}`,
                            background: COLORS.secondary,
                            color: '#fff',
                            fontSize: '13px',
                            fontWeight: 600,
                            fontFamily: FONTS_FAMILY.bold.body,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = COLORS.primary;
                            e.currentTarget.style.borderColor = COLORS.primary;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = COLORS.secondary;
                            e.currentTarget.style.borderColor = COLORS.secondary;
                        }}
                    >
                        + Nueva Reclamación
                    </button>
                </div>

                {/* Claims List */}
                <div style={{ padding: '20px 24px' }}>
                    {claimsData?.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                            {claimsData.map((claim, index) => {
                                const statusStyle = getStatusStyle(claim.status);
                                return (
                                    <div
                                        key={claim.id || index}
                                        style={{
                                            background: '#f8fafc',
                                            borderRadius: '12px',
                                            border: '1px solid #e2e8f0',
                                            padding: '18px 20px',
                                            transition: 'box-shadow 0.2s ease',
                                        }}
                                    >
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: '14px',
                                            flexWrap: 'wrap',
                                            gap: '8px',
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <span style={{
                                                    fontSize: '11px',
                                                    color: '#94a3b8',
                                                    fontFamily: FONTS_FAMILY.bold.body,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.04em',
                                                }}>
                                                    Reclamación #{claim.id}
                                                </span>
                                            </div>
                                            <span style={{
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                fontFamily: FONTS_FAMILY.bold.body,
                                                padding: '3px 12px',
                                                borderRadius: '12px',
                                                background: statusStyle.bg,
                                                color: statusStyle.color,
                                                border: `1px solid ${statusStyle.border}`,
                                            }}>
                                                {claim.status}
                                            </span>
                                        </div>

                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8', fontWeight: 600, fontFamily: FONTS_FAMILY.bold.body, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>
                                                    Queja
                                                </p>
                                                <p style={{ margin: 0, fontSize: '14px', color: COLORS.secondary, fontFamily: FONTS_FAMILY.regular.body }}>
                                                    {issuesData?.find((issue) => issue.value === claim.issue_id)?.label}
                                                </p>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8', fontWeight: 600, fontFamily: FONTS_FAMILY.bold.body, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>
                                                    Observación
                                                </p>
                                                <p style={{ margin: 0, fontSize: '14px', color: COLORS.secondary, fontFamily: FONTS_FAMILY.regular.body }}>
                                                    {claim.description}
                                                </p>
                                            </Grid>
                                            {claim?.company_name && (
                                                <Grid item xs={12} sm={6}>
                                                    <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8', fontWeight: 600, fontFamily: FONTS_FAMILY.bold.body, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>
                                                        Empresa
                                                    </p>
                                                    <p style={{ margin: 0, fontSize: '14px', color: COLORS.secondary, fontFamily: FONTS_FAMILY.regular.body }}>
                                                        {claim.company_name}
                                                    </p>
                                                </Grid>
                                            )}
                                            <Grid item xs={12} sm={6}>
                                                <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8', fontWeight: 600, fontFamily: FONTS_FAMILY.bold.body, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px' }}>
                                                    Fecha de creación
                                                </p>
                                                <p style={{ margin: 0, fontSize: '14px', color: COLORS.secondary, fontFamily: FONTS_FAMILY.regular.body }}>
                                                    {format(new Date(claim.created_at.replace(" ", "T")), "dd 'de' MMMM yyyy", { locale: es })}
                                                </p>
                                            </Grid>
                                        </Grid>

                                        {claim.solution && (
                                            <div style={{
                                                marginTop: '14px',
                                                padding: '12px 16px',
                                                background: '#dcfce7',
                                                borderRadius: '8px',
                                                border: '1px solid #86efac',
                                            }}>
                                                <p style={{ margin: 0, fontSize: '11px', color: '#15803d', fontWeight: 600, fontFamily: FONTS_FAMILY.bold.body, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>
                                                    Respuesta
                                                </p>
                                                <p style={{ margin: 0, fontSize: '14px', color: '#166534', fontFamily: FONTS_FAMILY.regular.body }}>
                                                    {claim.solution}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '40px 20px',
                            color: '#94a3b8',
                        }}>
                            <span style={{ fontSize: '40px', marginBottom: '12px', opacity: 0.5 }}>📭</span>
                            <p style={{
                                margin: 0,
                                fontSize: '16px',
                                fontWeight: 600,
                                fontFamily: FONTS_FAMILY.bold.body,
                                color: '#64748b',
                            }}>
                                No hay reclamaciones
                            </p>
                            <p style={{
                                margin: '4px 0 0',
                                fontSize: '13px',
                                fontFamily: FONTS_FAMILY.regular.body,
                            }}>
                                Si tiene alguna queja sobre esta solicitud, puede crear una nueva reclamación.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ComplaintsAndClaims;
